import * as accountLibraryActions from './account-library.actions';
import * as clientLibraryActions from './../client-library/client-library.actions';

import { Store, select } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  FormCategoryService,
  FormRequestOptions,
  FormService,
  FormTemplateService,
  getPersonalizedPlanStatusErrorMessage,
} from '@nrwl/rx-service-forms';
import { forkJoin, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { ClientLibraryState } from '../client-library/client-library.state';
import { Client, Form, FormTemplate } from '@nrwl/rx-models';
import { Injectable, inject } from '@angular/core';
import { PatientsService } from '@nrwl/rx-service-patients';
import { SnackbarService } from '@nrwl/snackbar-services';
import { getState } from './account-library.selector';
import { FeatureFlagsStore, FeatureKey, FormTemplateTypesService } from '../feature-flags';

@Injectable()
export class AccountLibraryStoreEffects {
  private actions$ = inject(Actions);
  private store = inject(Store<ClientLibraryState>);
  private formTemplateService = inject(FormTemplateService);
  private formCategoryService = inject(FormCategoryService);
  public snackbarService = inject(SnackbarService);
  private patientsService = inject(PatientsService);
  private formService = inject(FormService);
  private featureFlagsStore = inject(FeatureFlagsStore);
  private formTemplateTypes = inject(FormTemplateTypesService);

  loadAccountLibrary = createEffect(() =>
    this.actions$.pipe(
      ofType<accountLibraryActions.LoadAccountLibrary>(
        accountLibraryActions.AccountLibraryActionTypes.LoadAccountLibrary,
      ),
      withLatestFrom(this.store.pipe(select(getState))),
      mergeMap(([action, store]) =>
        this.formTemplateTypes.getVisibleTypesWhenReady().pipe(
          switchMap((typeFilter) => {
            let templates$ = this.formTemplateService.getFormTemplatesAccountLibraryEx(
              action.payload,
              typeFilter,
            );

            if (store.formTemplates && store.formTemplatesTypeFilter === typeFilter) {
              templates$ = of(store.formTemplates);
            }

            return forkJoin([templates$]).pipe(
              map(([templates]) => {
                const categories = templates
                  .map((item) => item.category)
                  .filter((q) => !q.is_deleted)
                  .sort((a, b) => (a.order > b.order ? 1 : -1));
                return new accountLibraryActions.LoadAccountLibrarySuccess({
                  templates,
                  rawTemplates: templates,
                  categories,
                  typeFilter,
                });
              }),
              catchError((err: any) => of(new accountLibraryActions.Error(err))),
            );
          }),
        ),
      ),
    ),
  );

  shareViaPortal = createEffect(() =>
    this.actions$.pipe(
      ofType<accountLibraryActions.ShareViaPortal>(
        accountLibraryActions.AccountLibraryActionTypes.ShareViaPortal,
      ),
      mergeMap(({ payload }) => {
        if (payload.email) {
          return this.patientsService
            .updatePatient(payload.httpData.forms[0].patient, {
              email: payload.email,
            })
            .pipe(
              switchMap((client: Client) => {
                this.store.dispatch(
                  new clientLibraryActions.LoadPatientSuccess({ patient: client }),
                );
                return this.formService.postBulk(payload.httpData);
              }),
              map((res: any) => {
                this.snackbarService.success('Form(s) successfully shared via RxPortal');
                res.forEach((item) =>
                  this.store.dispatch(new clientLibraryActions.CreateForm(item)),
                );
                return new accountLibraryActions.ShareSuccess(res);
              }),
              catchError((err: any) => {
                this.snackbarService.error('Something went wrong. Please try again later.');
                return of(new accountLibraryActions.Error(err));
              }),
            );
        }
        return this.formService.postBulk(payload.httpData).pipe(
          map((res: any) => {
            this.snackbarService.success('Form(s) successfully shared via RxPortal');
            res.forEach((item) => this.store.dispatch(new clientLibraryActions.CreateForm(item)));
            return new accountLibraryActions.ShareSuccess(res);
          }),
          catchError((err: any) => {
            this.snackbarService.error('Something went wrong. Please try again later.');
            return of(new accountLibraryActions.Error(err));
          }),
        );
      }),
    ),
  );

  shareViaLink = createEffect(() =>
    this.actions$.pipe(
      ofType<accountLibraryActions.ShareViaLink>(
        accountLibraryActions.AccountLibraryActionTypes.ShareViaLink,
      ),
      mergeMap(({ payload }) => {
        if (payload.phone_number) {
          return this.patientsService
            .updatePatient(payload.httpData.forms[0].patient, {
              phone_number: payload.phone_number,
            })
            .pipe(
              switchMap((client: Client) => {
                this.store.dispatch(
                  new clientLibraryActions.LoadPatientSuccess({ patient: client }),
                );
                return this.formService.postBulk(payload.httpData);
              }),
              map((res: any) => {
                this.snackbarService.success('Form(s) successfully shared');
                res.forEach((item) =>
                  this.store.dispatch(new clientLibraryActions.CreateForm(item)),
                );
                return new accountLibraryActions.ShareSuccess(res);
              }),
              catchError((err: any) => {
                this.snackbarService.error('Something went wrong. Please try again later.');
                return of(new accountLibraryActions.Error(err));
              }),
            );
        }
        return this.formService.postBulk(payload.httpData).pipe(
          map((res: any) => {
            this.snackbarService.success('Form(s) successfully shared');
            res.forEach((item) => this.store.dispatch(new clientLibraryActions.CreateForm(item)));
            return new accountLibraryActions.ShareSuccess(res);
          }),
          catchError((err: any) => {
            this.snackbarService.error('Something went wrong. Please try again later.');
            return of(new accountLibraryActions.Error(err));
          }),
        );
      }),
    ),
  );

  // No need to load form_data, it's taken care by rx-form-presenter lib
  selectFormTemplate = createEffect(() =>
    this.actions$.pipe(
      ofType<accountLibraryActions.SelectFormTemplate>(
        accountLibraryActions.AccountLibraryActionTypes.SelectFormTemplate,
      ),
      filter((action) => action.payload.form._selected),
      mergeMap((action: any) => {
        return this.formTemplateService
          .getExpandedFormTemplateByID(action.payload.form.id, action.payload.patientId)
          .pipe(
            map(
              (formTemplate: any) =>
                new accountLibraryActions.SelectFormTemplateSuccess(formTemplate),
            ),
            catchError((err: any) => of(new accountLibraryActions.Error(err))),
          );
      }),
    ),
  );

  // No need to load form_data, it's taken care by rx-form-presenter lib
  serPreviewTemplate = createEffect(() =>
    this.actions$.pipe(
      ofType<accountLibraryActions.SetPreviewTemplate>(
        accountLibraryActions.AccountLibraryActionTypes.SetPreviewTemplate,
      ),
      //filter((action) => action.payload.form._selected),
      // map(action => new accountLibraryActions.SetPreviewTemplateSuccess(action.payload.form.id, action.payload.patientId))
      mergeMap((action: any) => {
        return this.formTemplateService
          .getExpandedFormTemplateByID(action.payload.form.id, action.payload.patientId)
          .pipe(
            map(
              (formTemplate: any) =>
                new accountLibraryActions.SetPreviewTemplateSuccess(formTemplate),
            ),
            catchError((err: any) => of(new accountLibraryActions.Error(err))),
          );
      }),
    ),
  );

  setEditableTemplates = createEffect(() =>
    this.actions$.pipe(
      ofType<accountLibraryActions.SetEditableTemplates>(
        accountLibraryActions.AccountLibraryActionTypes.SetEditableTemplates,
      ),
      mergeMap((action: any) => {
        const requests = action.payload.map((item) =>
          this.formTemplateService.getFormTemplateById(item.id),
        );

        return forkJoin(requests).pipe(
          map(
            (formTemplates: FormTemplate[] | any) =>
              new accountLibraryActions.SetEditableTemplatesSuccess(formTemplates),
          ),
          catchError((err: any) => of(new accountLibraryActions.Error(err))),
        );
      }),
    ),
  );

  fillSignTemplate = createEffect(() =>
    this.actions$.pipe(
      ofType<accountLibraryActions.FillSign>(
        accountLibraryActions.AccountLibraryActionTypes.FillSign,
      ),
      withLatestFrom(this.store.pipe(select(getState))),
      mergeMap(([action, state]) => {
        const data = {
          patient: action.payload.data.patientId,
          shared_with_patient: false,
          template: action.payload.id,
          title: action.payload.data?.name,
          assigned_provider: null,
          publication_date: new Date().toISOString(),
          fill_n_sign: false,
          form_data: action.payload.form_data,
          signees: action.payload.data.signees,
        };

        if (
          action.payload.data.metadata?.signees ||
          action.payload.data.metadata?.medical_history ||
          action.payload.data.metadata?.medical_notes ||
          action.payload.data.metadata?.demographics ||
          action.payload.data.metadata?.primary_insurance ||
          action.payload.data.metadata?.secondary_insurance ||
          action.payload.data.metadata?.tertiary_insurance ||
          action.payload.data.metadata?.shots ||
          action.payload.data.metadata?.appointment_id ||
          (action.payload.data.metadata?.location_remote_id !== null &&
            action.payload.data.metadata?.location_remote_id !== undefined &&
            action.payload.data.metadata?.location_remote_id !== '') ||
          action.payload.data.metadata?.treatment_id ||
          action.payload.data.metadata?.treatment_notes
        ) {
          data['metadata'] = action.payload.data.metadata;
        }

        const existingForm = state.savedForms.find((item: Form) => item.id === action.payload.id);

        if (existingForm) {
          const updatePayload: any = {
            form_data: action.payload.form_data,
          };

          if (action.payload.data?.metadata) {
            updatePayload.metadata = action.payload.data.metadata;
          }

          return this.formService.update(existingForm.id, updatePayload).pipe(
            switchMap((res: any) => {
              this.snackbarService.success('Form(s) submitted');
              this.store.dispatch(new clientLibraryActions.FillSignSuccess(res));
              return action.payload.data.status
                ? [
                    new accountLibraryActions.FillSignSuccess(
                      res,
                      action.payload.scribeArtifactOperationId,
                    ),
                    new accountLibraryActions.FinalisationFillSign(action.payload.data.status),
                  ]
                : [
                    new accountLibraryActions.FillSignSuccess(
                      res,
                      action.payload.scribeArtifactOperationId,
                    ),
                  ];
            }),
            catchError((err: unknown) => {
              this.notifyPersonalizedPlanStatusError(err);

              return of(
                new accountLibraryActions.FillSignFailure(
                  err,
                  action.payload.scribeArtifactOperationId,
                ),
                new accountLibraryActions.Error(err as string),
              );
            }),
          );
        } else {
          return this.formService.create(data).pipe(
            switchMap((res: Form) => {
              this.snackbarService.success('Form(s) submitted');
              this.store.dispatch(new clientLibraryActions.CreateForm(res));
              return action.payload.data.status
                ? [
                    new accountLibraryActions.FillSignSuccess(
                      res,
                      action.payload.scribeArtifactOperationId,
                    ),
                    new accountLibraryActions.FinalisationFillSign(action.payload.data.status),
                  ]
                : [
                    new accountLibraryActions.FillSignSuccess(
                      res,
                      action.payload.scribeArtifactOperationId,
                    ),
                  ];
            }),
            catchError((err: unknown) => {
              this.notifyPersonalizedPlanStatusError(err);

              return of(
                new accountLibraryActions.FillSignFailure(
                  err,
                  action.payload.scribeArtifactOperationId,
                ),
                new accountLibraryActions.Error(err as string),
              );
            }),
          );
        }
      }),
    ),
  );

  finalisationFillSignTemplate = createEffect(() =>
    this.actions$.pipe(
      ofType<accountLibraryActions.FinalisationFillSign>(
        accountLibraryActions.AccountLibraryActionTypes.FinalisationFillSign,
      ),
      withLatestFrom(this.store.pipe(select(getState))),
      mergeMap(([action, state]) => {
        this.snackbarService.info('Updating Form(s)...');

        const payload = {
          ids: state.savedForms.map((item: Form) => item.id),
          status: action.payload.status,
          fill_n_sign: false,
          shared_with_patient: false,
        };

        return this.formService.updateBulk(payload, this.getFormRequestOptions()).pipe(
          switchMap((res: any) => {
            this.snackbarService.success('Form(s) submitted');
            return [
              new accountLibraryActions.FinalisationFillSignSuccess(res),
              new clientLibraryActions.ChangeFormsStatusBulkSuccess({
                ...payload,
                forms: res,
              }),
            ];
          }),
          catchError((err: any) => {
            this.notifyPersonalizedPlanStatusError(err);
            return of(new accountLibraryActions.Error(err));
          }),
        );
      }),
    ),
  );

  private getFormRequestOptions(): FormRequestOptions {
    return this.featureFlagsStore.has(FeatureKey.PERSONALIZED_PLANS) ? { formType: 'all' } : {};
  }

  private notifyPersonalizedPlanStatusError(error: unknown): void {
    const message = getPersonalizedPlanStatusErrorMessage(error);

    if (message) {
      this.snackbarService.error(message);
    }
  }
}
